import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {ConfirmationService} from 'primeng/api';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {ObjectRequestList} from 'features/core/shared/dto/object/request/object-request-list';
import {DtoList} from 'features/core/shared/dto/dto.list';
import {CoreApiResponseDto} from 'features/core/shared/dto/core-api-response.dto';
import {JbSpringBatchIndicatorService} from 'features/feature-tech/jb/service/jb-spring-batch-indicator.service';
import {ProvisionRunDto} from '../../../model/dto/entity/provision-run.dto';
import {ProvisionRunStatus} from '../../../enum/provision-run-status.enum';
import {CP_RUN_STATUS_ORDER} from '../../../enum/provision-run-status-order';
import {getProvisionRunStatusLabel} from '../../../enum/provision-run-status-label';
import {PROVISION_RUN_STATUS_FAILED_ANCHOR} from '../../../model/provision-run-status-presentation';
import {CpProvisionRunService} from '../../../service/api/cp-provision-run.service';
import {CpFeatureRight} from '../../../service/domain/cp-feature-right';
import {cpServerMessage} from '../../../service/domain/cp-server-message';
import {CpRunActivationSummaryService} from '../../../service/domain/cp-run-activation-summary.service';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';

/**
 * One operator control of a timeline row.
 *
 * Shape and semantics mirror `ActionButton` of the bm original
 * (`bm-billing-run-status-flow.component.ts`) so the two step bars stay recognisably the same
 * component family: `disabled` is a SNAPSHOT taken while the timeline is built, which is why every
 * `command` re-checks its own eligibility before it fires (see handleCalculate() and friends).
 */
export interface CpRunActionButton {
    label: string;
    icon?: string;
    command?: () => void;
    disabled?: boolean;
    /** Confirm text shown to the operator; the command itself owns the ConfirmationService call. */
    dialogConfirm?: string;
    /** Marks the action that carries the run into the NEXT step (rendered as the primary button). */
    nextStep?: boolean;
    /**
     * A READ-ONLY action — it fetches a document and changes nothing on the run.
     *
     * Such an action is deliberately NOT gated by {@link CpProvisionRunStatusFlowComponent.isActionBlocked}:
     * neither the run right `cp_runExecution` (the endpoint behind it checks object READ access, see
     * handleAccountingExport()) nor a running `cp%` job has anything to say about reading a document
     * of a run that is already paid out. Blocking it on the shared gate would take the accounting
     * hand-over away from a clerk who may read the run, for the duration of a job belonging to a
     * DIFFERENT run — the job indicator is module-wide, not per run.
     */
    readOnly?: boolean;
    /** Key this button spins on while its own request is in flight (matched against `busyAction`). */
    loadingKey?: string;
    /**
     * A STATE-CHANGING action guarded by its OWN feature right instead of `cp_runExecution`
     * (today: "Abrechnungen erstellen", right `cp_agentStatement`).
     *
     * It is still dead while an action is in flight or a `cp%` job runs, but a missing run right
     * does not lock it — a clerk may hold `cp_agentStatement` without `cp_runExecution`. Its own
     * grant (optimistic + sticky 403 demotion) is folded into `disabled`. See
     * {@link CpProvisionRunStatusFlowComponent.isActionDisabled}.
     */
    ownRight?: boolean;
}

/**
 * One row of the four-step bar of 04-frontend.md §5.1.
 *
 * `status` is DERIVED from the position of the run status inside CP_RUN_STATUS_ORDER — never from
 * the enum ordinal (see the class TSDoc of CpProvisionRunStatusFlowComponent).
 */
export interface CpRunTimelineEvent {
    step: number;
    title: string;
    description?: string;
    status: 'pending' | 'active' | 'completed';
    markerClass: string;
    icon: string;
    /**
     * The FAILED status that belongs to THIS step (04 §5.2). While it is the current status the row
     * renders an error badge plus {@link restartAction}; it is never a step of its own.
     */
    failedStatus?: ProvisionRunStatus;
    /**
     * Restart control of a failed step — the SAME endpoint that produced the failure (04 §5.1).
     * Rendered only while {@link failedStatus} is the current status, in its own error cell, so it
     * can never be confused with the forward actions of the row.
     */
    restartAction?: CpRunActionButton;
    actions?: CpRunActionButton[];
}

/**
 * Step bar of a Provisionslauf — the "Übersicht" tab of `/#/cp/dashboard/run/{provisionRunId}`
 * (04-frontend.md §5.1/§5.2, PAT §J.2).
 *
 * Built in the form of the repo original `BmBillingRunStatusFlowComponent`
 * (`features/feature-bm/bm/component/protected-components/bm-billing-run-status-flow-component/`):
 * a list of {@link CpRunTimelineEvent} rows rendered as a table, each row deriving
 * pending/active/completed from the position of the current status inside an EXPLICIT business
 * order, each action a {@link CpRunActionButton} whose command asks the ConfirmationService first
 * and then POSTs to the run endpoint, and an auto refresh driven by the transition
 * running -> not-running of the globally mounted jb job indicator.
 *
 * ── DOCUMENTED DEVIATION: RAW `<table>` INSTEAD OF `p-table` (UX1)
 *
 * The step bar is rendered as a plain `<table>` with PrimeFlex utility classes, one row per step —
 * NOT as a PrimeNG `p-table`. fe_core_ux-guidelines UX1 ("every component from PrimeNG") would ask
 * for the latter; the build order here is the reference module, and
 * `features/feature-bm/bm/component/protected-components/bm-billing-run-status-flow-component/bm-billing-run-status-flow.component.html:14`
 * is a raw `<table class="w-full" style="border-collapse: collapse;">` with exactly this row shape.
 * The parity is deliberate and is the reason the two step bars are readable as one component
 * family; it is recorded here so the deviation is on the record rather than mistaken for an
 * oversight. Nothing else in this component departs from UX1 — every control is a `p-button`, every
 * banner a `p-message`, and the whole layout is PrimeFlex.
 *
 * ── ORDER: CP_RUN_STATUS_ORDER, NEVER THE ENUM ORDINAL (04 §5.2, U-09, binding)
 *
 * CALCULATION_FAILED(9), ACTIVATION_FAILED(10), PAYOUT_FAILED(11) and ABORTED(12) carry the HIGHEST
 * ordinals because they were appended last, but they are reachable from EARLY business steps. A
 * progress derivation over ordinals would therefore report a run that failed during calculation as
 * standing behind COMPLETED(8) — the whole bar green, every action gone, on a run that produced
 * nothing. So:
 *   * the linear bar is rendered from CP_RUN_STATUS_ORDER (9 statuses, NEW … COMPLETED),
 *   * the three FAILED statuses are ANCHORED onto the step they interrupted (the shared
 *     {@link PROVISION_RUN_STATUS_FAILED_ANCHOR}) and shown as an error badge with a restart button
 *     on that step,
 *   * ABORTED resolves to NO index at all and is shown as a terminal banner above the bar.
 * The status-bar test asserts against CP_RUN_STATUS_ORDER, not against ordinals (DA-017.6).
 *
 * ── RIGHTS (`cp_runExecution`, 04 §5.1 / U-08) — OPTIMISTIC + 403 DEMOTION
 *
 * DOCUMENTED DEVIATION from 04 §5.1 ("Alle Buttons nur mit um-Recht `cp_runExecution` aktiv"),
 * orchestrator decision OD-1, applied identically to all four cp rights. The full reasoning lives
 * in ONE place — {@link CpFeatureRight} — and is summarised here:
 *
 *   * this frontend has NO feature-rights service (no hasRight/hasPermission/featureRight anywhere
 *     in `features/core/shared`), and no cp backend payload carries a grant flag either;
 *   * so a literal "default false" is not a safe default, it is a permanently inert button — a
 *     lost function, not a closed hole, because the frontend was never the security boundary;
 *   * the boundary is `cp/cross/um/ProvisionFeatureGuard`, which is itself DEFAULT-ALLOW and
 *     refuses only on an explicitly seeded `deny`. An optimistic bar therefore matches the
 *     server's own polarity instead of contradicting it.
 *
 * Hence {@link runExecutionGranted} starts GRANTED and the bar is live. A 403 from an action
 * endpoint is the right answering the hard way: it demotes the grant for this component instance
 * ({@link runExecutionDeniedByServer}, sticky — a later re-read of the run cannot re-enable
 * buttons the server has already refused), and a German `p-message` says so.
 *
 * WHAT STILL WINS OVER THE DEFAULT — the optimistic value is a fallback, never an override, so the
 * component is already wired for the day a real answer exists:
 *   * a host that resolved the grant itself passes {@link runExecutionAllowed} true/false;
 *   * a backend that later ships the grant on the run payload is read defensively as
 *     `runExecutionAllowed` there.
 * Both are the shape the two house precedents use — `ch` carries `ChInboxCounts.canAccessAll`
 * (`features/feature-crm/ch/page/ch-inbox/ch-inbox.page.ts:1039`) and `cw` carries
 * `CwStepProcessingPlanDto.allowedActions` (`features/feature-crm/cw/page/dashboard/
 * cw-case-dashboard/components/cw-case-workbench-detail/cw-case-workbench-detail.component.ts:541`)
 * — and both are BACKEND-EVALUATED grants handed down with a payload the view already loads.
 * Neither module queries `um.RoleAuthModuleFeature` from the browser, and this one must not
 * either: the house evaluation short-circuits admins and folds wildcard-module entries by highest
 * ordinal (see the "Admin note" of `cw/cross/um/CwFeatureGuard`), so a re-implementation in the FE
 * would answer "denied" for exactly the users who hold the right.
 *
 * The resolution order lives in {@link CpFeatureRight#resolve} and is applied by
 * {@link resolveRunExecutionRight}: server 403 > host answer > payload answer > optimistic default.
 *
 * ── THE ACCOUNTING HAND-OVER (step 4, 03 §8.4 step 4 / BR-080 / original requirement Z.261)
 *
 * Step 4 carries one action that is NOT a status transition: "Buchhaltungs-Meldung herunterladen"
 * fetches `GET /{id}/accountingExport?format=csv` and saves it. It sits on step 4 because that is
 * where the document comes into existence — 04 §5.1 lists IN_PAYOUT, PAID_OUT, PAYOUT_FAILED and
 * COMPLETED as the statuses of "Verträge ausgezahlt", and 03 §8.4 step 4 makes the hand-over the
 * PRECONDITION of the manual `COMPLETED` ("`COMPLETED` setzt der Sachbearbeiter nach Übergabe
 * manuell"). Download and "Abschließen" therefore stand side by side, in the order the clerk walks
 * them, and the run stays reachable for the download after it was completed — a hand-over document
 * that vanishes the moment the run is closed could never be re-issued to the bookkeeper.
 *
 * Two consequences follow from it being a READ:
 *   * it is offered on PAID_OUT and COMPLETED only ({@link isEligibleAccountingExport}) — during
 *     IN_PAYOUT the payout is still writing rows, and PAYOUT_FAILED means an INCOMPLETE payout, so
 *     neither may be handed to accounting as "what was paid";
 *   * it is not gated by the run right or by a running job (see `readOnly` on
 *     {@link CpRunActionButton}), and it asks no confirmation — nothing is changed by it.
 * A 403 is still answered the module way: the button demotes stickily and says so in German.
 *
 * ── THE AGENT STATEMENTS (step 4, cp-agent-statement, right `cp_agentStatement`)
 *
 * "Abrechnungen erstellen" sits next to the accounting hand-over and starts the job that issues the
 * FINAL statement (Provisionsabrechnung) of every agent with movement in the run
 * (`POST /{id}/agentStatements/create`). Offered on PAID_OUT and COMPLETED only — the same status
 * set and the same reasoning as {@link isEligibleAccountingExport} (GR-S1: PAYOUT_FAILED is an
 * incomplete payout). The job is idempotent (GR-S9): existing ACTIVE statements are not
 * overwritten, which the confirmation says in German. Its right is `cp_agentStatement`, not
 * `cp_runExecution`, with the module's optimistic default and its OWN sticky 403 demotion
 * ({@link agentStatementDeniedByServer}) — a refusal locks this button only.
 *
 * ── DIALOGS
 *
 * Every action is confirmed through the shared PrimeNG `ConfirmationService` (the `<p-confirmdialog>`
 * host sits in the app shell, `app.main.component.html`) — the same mechanism the bm original uses.
 * No hand-written form HTML exists here and none is needed: these actions carry NO payload, they are
 * pure POSTs on the run id. (The three PAYLOAD dialogs of the cp module — /manualBooking, /transfer,
 * /changeAgent — belong to the agent OBC of 04 §7 and are metadata-driven forms there, not here.)
 */
@Component({
    selector: 'cp-provision-run-status-flow',
    templateUrl: './cp-provision-run-status-flow.component.html',
    styleUrls: ['./cp-provision-run-status-flow.component.scss'],
    standalone: false
})
export class CpProvisionRunStatusFlowComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;
    /** Which STATE-CHANGING action is currently in flight — drives the per-button spinner. */
    busyAction: string | null = null;
    /**
     * Which READ-ONLY action is currently in flight (today: the accounting hand-over download).
     *
     * Deliberately its own field rather than a second user of {@link busyAction}: a document
     * download runs while a transition of the bar may also be running, and one shared field would
     * make the two steal each other's spinner.
     */
    busyReadOnlyAction: string | null = null;
    /** True while the jb indicator reports a running `cp%` job; disables every action. */
    jobRunning: boolean = false;

    @Input() provisionRunDto: ProvisionRunDto;
    @Input() provisionRunId: number;
    /**
     * Grant of the um right `cp_runExecution` AS THE HOST KNOWS IT.
     *
     * `null` (the default) means "the host does not know" — which is NOT "denied": the grant is
     * then read from the run payload by {@link resolveRunExecutionRight}, and if that is silent
     * too the optimistic default applies (RIGHTS section of the class TSDoc). A host that resolved
     * the grant itself passes true/false and wins over both.
     */
    @Input() runExecutionAllowed: boolean | null = null;

    /**
     * The RESOLVED grant — the ONE value the template and {@link isActionBlocked} read.
     *
     * Starts GRANTED: nothing in this frontend can raise it, so a false default would leave the
     * whole bar permanently dead. See the RIGHTS section of the class TSDoc and
     * {@link CpFeatureRight}.
     */
    runExecutionGranted: boolean = true;

    /** Emitted whenever this component re-read the run, so the host can refresh its own tabs. */
    @Output() provisionRunDtoChange = new EventEmitter<ProvisionRunDto>();

    events: CpRunTimelineEvent[];

    /** Visibility of the activation confirmation dialog (replaces the one-line confirm prompt). */
    activationDialogVisible: boolean = false;

    /**
     * Error figures of the run for the "Fehler" column — `null` until loaded or when the read
     * failed (the cell then shows a dash, never a 0 that was not measured).
     */
    errorContractCount: number | null = null;
    openErrorMessageCount: number | null = null;
    openRunErrorMessageCount: number | null = null;

    private statusSubscription: Subscription;
    private errorCountSubscription: Subscription;
    /** "Abrechnungen erstellen" request in flight — tracked so ngOnDestroy can drop it. */
    private agentStatementsSubscription: Subscription;
    private jobWasRunning: boolean = false;
    private pendingTimeouts: number[] = [];

    /**
     * Set once an action endpoint answered 403. A server that has refused the right does not become
     * more permissive on the next run re-read, so this demotion is STICKY: without it
     * {@link resolveRunExecutionRight} would re-enable the buttons from a stale payload flag.
     */
    private runExecutionDeniedByServer: boolean = false;

    /**
     * Set once the accounting-export endpoint answered 403. That endpoint checks object READ access
     * on the run, not the feature right, so it demotes its OWN button and leaves the rest of the bar
     * alone — sticky for the same reason as {@link runExecutionDeniedByServer}.
     */
    private accountingExportDeniedByServer: boolean = false;

    /**
     * Set once the agent-statement job endpoint answered 403 (right `cp_agentStatement`). Demotes the
     * "Abrechnungen erstellen" button only — sticky for the same reason as
     * {@link runExecutionDeniedByServer}.
     */
    private agentStatementDeniedByServer: boolean = false;

    constructor(
        protected provisionRunService: CpProvisionRunService,
        protected confirmationService: ConfirmationService,
        protected springBatchIndicatorService: JbSpringBatchIndicatorService,
        protected activationSummaryService: CpRunActivationSummaryService,
        protected messageService: MvsMessageService
    ) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize component.
     */
    initComponent(): void {

        if (this.provisionRunDto) {
            this.provisionRunId = this.provisionRunDto.id;
            this.refreshComponent();
        } else if (this.provisionRunId) {
            this.retrieveProvisionRun();
        }

        // The dashboard shell (CpDashboardPage) already activates the indicator for 'cp%'. Activating
        // it again here keeps the component usable STANDALONE (widget test shell, or embedded outside
        // the shell) without ever fighting the page for the configuration.
        if (!this.springBatchIndicatorService.isActive) {
            this.springBatchIndicatorService.activate({
                jobNamePattern: 'cp%',
                pollIntervalMs: 60000,
                showStopButton: false,
                label: 'Provisions-Jobs'
            });
        }

        this.subscribeToJobCompletion();
    }

    /**
     * Refresh component — rebuild the timeline for the status the run currently rests on.
     */
    refreshComponent(): void {
        this.resolveRunExecutionRight();
        this.initEventsFlow();
        this.refreshErrorCounts();
        this.initialized = true;
    }

    /**
     * Read the error figures of the "Fehler" column: contracts with outcome ERROR, open ERROR
     * messages on contract level and on run level (one batched QL request, see
     * {@link CpRunActivationSummaryService#getCounts}).
     *
     * Re-read on every refresh, because a refresh follows every status change and every job end —
     * exactly the moments the figures change.
     */
    refreshErrorCounts(): void {

        this.errorCountSubscription?.unsubscribe();

        if (!this.provisionRunId) {
            return;
        }

        this.errorCountSubscription = this.activationSummaryService
            .getCounts(this.provisionRunId)
            .subscribe(counts => {
                this.errorContractCount = counts.errorContractCount;
                this.openErrorMessageCount = counts.openErrorMessageCount;
                this.openRunErrorMessageCount = counts.openRunErrorMessageCount;
            });
    }

    /**
     * Resolve the `cp_runExecution` grant — the ONE place that decides whether the bar has controls.
     *
     * The order of authority itself lives in {@link CpFeatureRight#resolve}, shared with the other
     * three cp rights: a server 403 (sticky) beats an explicit host answer, which beats the grant
     * carried on the run payload, which beats the optimistic default.
     *
     * The payload flag is read defensively because the grant belongs to the backend answer, not to
     * the persisted run entity — the same shape `ch` uses for `canAccessAll` on its counts payload.
     * The cp backend does not carry it today, so this resolves to the optimistic default and the
     * bar is live; the moment the backend ships the field, that answer wins without a code change
     * here.
     */
    private resolveRunExecutionRight(): void {

        const grantCarrier = this.provisionRunDto as unknown as { runExecutionAllowed?: boolean };

        this.runExecutionGranted = CpFeatureRight.resolve(
            this.runExecutionDeniedByServer,
            this.runExecutionAllowed,
            grantCarrier?.runExecutionAllowed);
    }

    /**
     * Subscribe to the jb indicator and re-read the run on the transition running -> not-running
     * (04-frontend.md §4: "business components subscribe to status$ and reload automatically on
     * the running→not-running transition" — German original: "Fachkomponenten abonnieren status$
     * und laden beim Übergang running→not-running automatisch neu").
     *
     * The run data is produced WHILE the jobs run (04 §5.1), so the status the page was opened with
     * goes stale the moment an action is triggered. Unlike the bm original this does NOT raise a
     * modal on job completion: the step bar sits inside a tabbed run detail whose six workbench tabs
     * refresh on the same signal, and a dialog on every job end would interrupt the reviewer in the
     * middle of a work list for information the bar itself already shows.
     */
    private subscribeToJobCompletion(): void {
        this.statusSubscription = this.springBatchIndicatorService.status$.subscribe(status => {
            const wasRunning = this.jobRunning;

            this.jobRunning = status.hasRunningJobs;

            if (status.hasRunningJobs) {
                this.jobWasRunning = true;
            } else if (this.jobWasRunning && wasRunning && !status.hasRunningJobs) {
                this.jobWasRunning = false;
                this.onJobsCompleted();
            }
        });
    }

    /**
     * All `cp%` jobs finished — go back to the slow poll interval and re-read the run.
     */
    private onJobsCompleted(): void {
        this.springBatchIndicatorService.updateConfig({pollIntervalMs: 60000});
        this.retrieveProvisionRun();
    }

    /**
     * Poll faster right after an action was triggered, so the operator sees the running job (and the
     * disabled buttons) within seconds instead of within a minute.
     */
    private setFastPollingMode(): void {
        this.springBatchIndicatorService.updateConfig({pollIntervalMs: 15000});
    }

    /**
     * Re-read the run and hand it up to the host.
     */
    retrieveProvisionRun(): void {

        if (!this.provisionRunId) {
            return;
        }

        const filter = FilterCriteria.create('id', FilterCriteria.cOperatorEqual, this.provisionRunId);
        const objectRequest = ObjectRequestList.createBasic(false, [filter], []);

        this.provisionRunService.list(objectRequest).subscribe((res: DtoList<ProvisionRunDto>) => {
            if (res.entries.length > 0) {
                this.provisionRunDto = res.entries[0] as ProvisionRunDto;
                this.provisionRunDtoChange.emit(this.provisionRunDto);
                this.refreshComponent();
            }
        });
    }

    /**
     * Build the four steps of 04-frontend.md §5.1.
     *
     * Step 4 covers IN_PAYOUT(6) and PAID_OUT(7) but deliberately NOT COMPLETED(8): COMPLETED sits
     * BEHIND both in CP_RUN_STATUS_ORDER, so the ordinary "current index > max check index" branch of
     * getStepStatus() turns the last step green on a finished run. Listing COMPLETED among the step's
     * own statuses instead would make getStepStatus() take the includes() branch and report 'active'
     * forever — the exact defect the bm original had to split its completion phase into two steps to
     * remove, and it would simply reappear here one step further on.
     */
    initEventsFlow(): void {

        const currentStatus = this.provisionRunDto.status;

        this.events = [
            {
                step: 1,
                title: 'Neu',
                description: 'Der Provisionslauf ist angelegt. Mit "Starte Berechnung" werden die '
                    + 'Verträge des Laufs bewertet und die Buchungen im Status "berechnet" abgelegt.',
                status: this.getStepStatus(currentStatus, [ProvisionRunStatus.NEW]),
                markerClass: this.getStepMarkerClass(currentStatus, [ProvisionRunStatus.NEW]),
                icon: 'fa-solid fa-circle-plus',
                // Only on NEW: once the first calculation ran, step 1 is done and the RE-calculation
                // lives on step 2 ("Neu berechnen"). Sharing the NEW/CALCULATED/IN_REVIEW gate here kept
                // "Starte Berechnung" clickable on a completed step - the bar contradicted itself.
                actions: [{
                    label: 'Starte Berechnung',
                    icon: 'fa-solid fa-calculator',
                    command: () => this.handleStartCalculation(),
                    disabled: !this.isEligibleStartCalculation(currentStatus),
                    nextStep: true
                }]
            },
            {
                step: 2,
                title: 'Verträge berechnen',
                description: 'Die berechneten Buchungen können geprüft und bei Bedarf neu berechnet '
                    + 'werden. Mit "Aktivieren" werden sie verbindlich auf die Abrechnungskonten gebucht.',
                status: this.getStepStatus(currentStatus, [
                    ProvisionRunStatus.IN_CALCULATION,
                    ProvisionRunStatus.CALCULATED,
                    ProvisionRunStatus.IN_REVIEW
                ]),
                markerClass: this.getStepMarkerClass(currentStatus, [
                    ProvisionRunStatus.IN_CALCULATION,
                    ProvisionRunStatus.CALCULATED,
                    ProvisionRunStatus.IN_REVIEW
                ]),
                icon: 'fa-solid fa-calculator',
                failedStatus: ProvisionRunStatus.CALCULATION_FAILED,
                restartAction: {
                    label: 'Berechnung erneut starten',
                    icon: 'fa-solid fa-rotate-right',
                    command: () => this.handleRestartCalculation()
                },
                actions: [
                    {
                        label: 'Neu berechnen',
                        icon: 'fa-solid fa-rotate',
                        command: () => this.handleCalculate(),
                        disabled: !this.isEligibleRecalculate(currentStatus)
                    },
                    {
                        label: 'Prüfen',
                        icon: 'fa-solid fa-magnifying-glass',
                        command: () => this.handleReview(),
                        disabled: !this.isEligibleReview(currentStatus)
                    },
                    {
                        label: 'Aktivieren',
                        icon: 'fa-solid fa-check',
                        command: () => this.handleActivate(),
                        disabled: !this.isEligibleActivate(currentStatus),
                        nextStep: true
                    }
                ]
            },
            {
                step: 3,
                title: 'Verträge aktiviert',
                description: 'Die Buchungen sind aktiv und wirken auf die Salden. Mit "Auszahlung '
                    + 'starten" werden die auszahlbaren Beträge zur Auszahlung gestellt.',
                status: this.getStepStatus(currentStatus, [
                    ProvisionRunStatus.IN_ACTIVATION,
                    ProvisionRunStatus.ACTIVATED
                ]),
                markerClass: this.getStepMarkerClass(currentStatus, [
                    ProvisionRunStatus.IN_ACTIVATION,
                    ProvisionRunStatus.ACTIVATED
                ]),
                icon: 'fa-solid fa-circle-check',
                failedStatus: ProvisionRunStatus.ACTIVATION_FAILED,
                restartAction: {
                    label: 'Aktivierung erneut starten',
                    icon: 'fa-solid fa-rotate-right',
                    command: () => this.handleRestartActivation()
                },
                actions: [{
                    label: 'Auszahlung starten',
                    icon: 'fa-solid fa-money-bill-transfer',
                    command: () => this.handlePayout(),
                    disabled: !this.isEligiblePayout(currentStatus),
                    nextStep: true
                }]
            },
            {
                step: 4,
                title: 'Verträge ausgezahlt',
                description: 'Die Auszahlungen sind gebucht. Laden Sie die Buchhaltungs-Meldung '
                    + 'herunter und übergeben Sie sie der Buchhaltung. Mit "Abrechnungen erstellen" '
                    + 'erhalten alle Berater mit Bewegung ihre finale Provisionsabrechnung. '
                    + 'Mit "Abschließen" wird der '
                    + 'Provisionslauf danach beendet und der nächste Lauf dieses Provisionstyps '
                    + 'freigegeben.',
                status: this.getStepStatus(currentStatus, [
                    ProvisionRunStatus.IN_PAYOUT,
                    ProvisionRunStatus.PAID_OUT
                ]),
                markerClass: this.getStepMarkerClass(currentStatus, [
                    ProvisionRunStatus.IN_PAYOUT,
                    ProvisionRunStatus.PAID_OUT
                ]),
                icon: 'fa-solid fa-money-bill-transfer',
                failedStatus: ProvisionRunStatus.PAYOUT_FAILED,
                restartAction: {
                    label: 'Auszahlung erneut starten',
                    icon: 'fa-solid fa-rotate-right',
                    command: () => this.handleRestartPayout()
                },
                actions: [
                    {
                        label: 'Buchhaltungs-Meldung herunterladen',
                        icon: 'fa-solid fa-file-arrow-down',
                        command: () => this.handleAccountingExport(),
                        disabled: !this.isEligibleAccountingExport(currentStatus),
                        readOnly: true,
                        loadingKey: 'accountingExport'
                    },
                    {
                        label: 'Abrechnungen erstellen',
                        icon: 'fa-solid fa-file-invoice',
                        command: () => this.handleCreateAgentStatements(),
                        disabled: !this.isEligibleCreateAgentStatements(currentStatus),
                        ownRight: true,
                        loadingKey: 'agentStatements'
                    },
                    {
                        label: 'Abschließen',
                        icon: 'fa-solid fa-flag-checkered',
                        command: () => this.handleComplete(),
                        disabled: !this.isEligibleComplete(currentStatus),
                        nextStep: true
                    }
                ]
            }
        ];
    }

    // ── Step state derivation ────────────────────────────────────────────────────────────────────

    /**
     * Position of a status inside the BUSINESS order, or -1 when the status is outside the bar.
     *
     * @param status status the run rests on
     */
    private resolveOrderIndex(status: ProvisionRunStatus): number {
        // PROVISION_RUN_STATUS_FAILED_ANCHOR is the module's ONE anchor table (see its TSDoc): the
        // step bar and the progress chip of the dashboard must place a failed run on the same step.
        const anchored = PROVISION_RUN_STATUS_FAILED_ANCHOR[status] ?? status;
        return CP_RUN_STATUS_ORDER.indexOf(anchored);
    }

    /**
     * Derive the state of one step from the position of the current status inside
     * CP_RUN_STATUS_ORDER.
     *
     * @param currentStatus status the run currently rests on
     * @param stepStatuses statuses covered by this step (FAILED statuses never belong here)
     */
    getStepStatus(
        currentStatus: ProvisionRunStatus,
        stepStatuses: ProvisionRunStatus[]
    ): 'pending' | 'active' | 'completed' {

        const currentIndex = this.resolveOrderIndex(currentStatus);

        // ABORTED (and any future status outside the bar): no step of the linear bar was reached, the
        // terminal banner carries the statement instead.
        if (currentIndex < 0) {
            return 'pending';
        }

        const checkIndexes = stepStatuses
            .map(status => CP_RUN_STATUS_ORDER.indexOf(status))
            .filter(index => index !== -1);

        if (checkIndexes.includes(currentIndex)) {
            return 'active';
        }

        if (currentIndex < Math.min(...checkIndexes)) {
            return 'pending';
        }

        if (currentIndex > Math.max(...checkIndexes)) {
            return 'completed';
        }

        return 'pending';
    }

    /**
     * Marker classes of the state derived by {@link getStepStatus} — the SAME derivation, so a step
     * can never report one state and be painted in the colour of another.
     *
     * @param currentStatus status the run currently rests on
     * @param stepStatuses statuses covered by this step
     */
    getStepMarkerClass(currentStatus: ProvisionRunStatus, stepStatuses: ProvisionRunStatus[]): string {

        switch (this.getStepStatus(currentStatus, stepStatuses)) {
            case 'completed':
                return 'bg-green-600 text-white border-circle';
            case 'active':
                return 'bg-blue-700 text-white border-circle';
            default:
                return 'surface-200 text-500 border-circle';
        }
    }

    /** True while the FAILED status of this step is the current status (error badge + restart). */
    isStepFailed(event: CpRunTimelineEvent): boolean {
        return !!event.failedStatus && this.provisionRunDto?.status === event.failedStatus;
    }

    /** True for a run that was aborted — terminal, no action of the bar is reachable any more. */
    isAborted(): boolean {
        return this.provisionRunDto?.status === ProvisionRunStatus.ABORTED;
    }

    /** German label of the current run status (shared with the run-detail header). */
    getStatusLabel(status: ProvisionRunStatus): string {
        return getProvisionRunStatusLabel(status);
    }

    /**
     * True when a still pending step carries at least one executable action, so its button is
     * reachable even before the run arrives on that step (the bm original's `hasEnabledAction`).
     */
    hasEnabledAction(event: CpRunTimelineEvent): boolean {
        return !!event.actions?.some(action => !action.disabled);
    }

    /** Every action of the bar is dead while a job runs, an action is in flight, or the right is missing. */
    isActionBlocked(): boolean {
        return this.busy || this.jobRunning || !this.runExecutionGranted;
    }

    /**
     * True while THIS button's own request is in flight — read-only actions spin on their own
     * field, state-changing ones on the shared {@link busyAction}.
     *
     * @param action the button being rendered
     */
    isActionLoading(action: CpRunActionButton): boolean {

        if (!action.loadingKey) {
            return false;
        }

        return action.readOnly
            ? this.busyReadOnlyAction === action.loadingKey
            : this.busyAction === action.loadingKey;
    }

    /**
     * The ONE disabled-rule of a button of the bar:
     *   * a read-only action is gated by its own snapshot only (no run right, no job gate);
     *   * an own-right action is dead while an action is in flight or a job runs, but NOT because
     *     `cp_runExecution` is missing — its own right lives in its `disabled` snapshot;
     *   * every other action is dead whenever {@link isActionBlocked} says so.
     *
     * @param action the button being rendered
     */
    isActionDisabled(action: CpRunActionButton): boolean {

        if (action.disabled) {
            return true;
        }

        if (action.readOnly) {
            return false;
        }

        if (action.ownRight) {
            return this.busy || this.jobRunning;
        }

        return this.isActionBlocked();
    }

    // ── "Fehler" column ─────────────────────────────────────────────────────────────────────────

    /**
     * Contract-level error figures belong to step 2 ("Verträge berechnen"): that is the step that
     * produces them and the one on which they can still be repaired ("Neu berechnen"). They are
     * shown from the moment the step was reached, and stay visible on a completed step 2 — a run
     * activated with open errors keeps saying so.
     */
    showsContractErrors(event: CpRunTimelineEvent): boolean {
        return event.step === 2 && event.status !== 'pending';
    }

    /**
     * Run-level error messages (`cp.ProvisionRunMessage`, e.g. a refused payout start) carry no
     * step of their own; they are shown on the step the run currently stands on, where the next
     * action is blocked by them.
     */
    showsRunErrors(event: CpRunTimelineEvent): boolean {
        return event.status === 'active' && (this.openRunErrorMessageCount ?? 0) > 0;
    }

    /** True when the figures of {@link showsContractErrors} have been read. */
    hasContractErrorFigures(): boolean {
        return this.errorContractCount != null && this.openErrorMessageCount != null;
    }

    // ── Eligibility (mirrors the transitions of 03-architektur-backend.md §11.1) ─────────────────

    /** The FIRST calculation — step 1 only, i.e. only while the run is still NEW. */
    isEligibleStartCalculation(status: ProvisionRunStatus): boolean {
        return status === ProvisionRunStatus.NEW;
    }

    /**
     * "Neu berechnen" of step 2 — only once a calculation exists (CALCULATED / IN_REVIEW). The
     * backend would accept NEW as well, but on a NEW run the bar offers the first calculation on
     * step 1; offering it twice, on two steps, is the contradiction the step bar must not show.
     */
    isEligibleRecalculate(status: ProvisionRunStatus): boolean {
        return [
            ProvisionRunStatus.CALCULATED,
            ProvisionRunStatus.IN_REVIEW
        ].includes(status);
    }

    /** CALCULATED -> IN_REVIEW is the only manual review transition. */
    isEligibleReview(status: ProvisionRunStatus): boolean {
        return [ProvisionRunStatus.CALCULATED].includes(status);
    }

    /** Activation starts from a finished calculation, whether or not it was put into review. */
    isEligibleActivate(status: ProvisionRunStatus): boolean {
        return [
            ProvisionRunStatus.CALCULATED,
            ProvisionRunStatus.IN_REVIEW
        ].includes(status);
    }

    /** Payout starts once the bookings are active. */
    isEligiblePayout(status: ProvisionRunStatus): boolean {
        return [ProvisionRunStatus.ACTIVATED].includes(status);
    }

    /** PAID_OUT -> COMPLETED is the manual closing transition. */
    isEligibleComplete(status: ProvisionRunStatus): boolean {
        return [ProvisionRunStatus.PAID_OUT].includes(status);
    }

    /**
     * The accounting hand-over exists from the moment the payout is BOOKED and stays available
     * afterwards (03 §8.4 step 4: the clerk sets COMPLETED manually AFTER the hand-over, so the
     * document must be there before that transition and re-obtainable after it).
     *
     * Deliberately NOT offered on IN_PAYOUT (the payout is still writing its rows — a document
     * pulled mid-job would report a part of the run as if it were the whole) and NOT on
     * PAYOUT_FAILED (the payout is incomplete by definition; what accounting gets must be what was
     * actually paid, and the run is restarted from the error cell instead). A sticky 403 from the
     * endpoint removes it as well.
     */
    isEligibleAccountingExport(status: ProvisionRunStatus): boolean {

        if (this.accountingExportDeniedByServer) {
            return false;
        }

        return [
            ProvisionRunStatus.PAID_OUT,
            ProvisionRunStatus.COMPLETED
        ].includes(status);
    }

    /**
     * The final agent statements exist only for a run whose payout is completely booked — PAID_OUT
     * and COMPLETED (GR-S1, same set as {@link isEligibleAccountingExport}). A sticky 403 of the job
     * endpoint (right `cp_agentStatement`) removes the action as well.
     */
    isEligibleCreateAgentStatements(status: ProvisionRunStatus): boolean {

        if (this.agentStatementDeniedByServer) {
            return false;
        }

        return [
            ProvisionRunStatus.PAID_OUT,
            ProvisionRunStatus.COMPLETED
        ].includes(status);
    }

    /**
     * Abort is allowed "up to and including IN_REVIEW / CALCULATION_FAILED" (04 §5.1) — i.e. exactly
     * as long as nothing has been activated. Once bookings are ACTIVE they are part of the agents'
     * balances and can no longer be thrown away by aborting the run.
     */
    isEligibleAbort(status: ProvisionRunStatus): boolean {
        return [
            ProvisionRunStatus.NEW,
            ProvisionRunStatus.IN_CALCULATION,
            ProvisionRunStatus.CALCULATED,
            ProvisionRunStatus.IN_REVIEW,
            ProvisionRunStatus.CALCULATION_FAILED
        ].includes(status);
    }

    // ── Actions ─────────────────────────────────────────────────────────────────────────────────

    handleCalculate(): void {
        this.confirmAndRun({
            eligible: this.isEligibleRecalculate(this.provisionRunDto?.status),
            blockedMessage: 'Der Provisionslauf kann in seinem aktuellen Status nicht neu berechnet werden. '
                + 'Neu berechnen ist erst nach der ersten Berechnung und nur vor der Aktivierung möglich.',
            header: 'Berechnung starten',
            message: 'Die Verträge des Provisionslaufs werden jetzt bewertet und die Buchungen im Status '
                + '"berechnet" abgelegt. Bereits berechnete Buchungen dieses Laufs werden dabei ersetzt. Fortfahren?',
            icon: 'fa-solid fa-calculator',
            acceptLabel: 'Ja, berechnen',
            actionKey: 'calculate',
            job: true,
            request: () => this.provisionRunService.calculate(this.provisionRunId)
        });
    }

    /** "Starte Berechnung" of step 1 — the same endpoint, gated on NEW only. */
    handleStartCalculation(): void {
        this.confirmAndRun({
            eligible: this.isEligibleStartCalculation(this.provisionRunDto?.status),
            blockedMessage: 'Die erste Berechnung ist nur im Status "Neu" möglich. Für eine erneute '
                + 'Berechnung verwenden Sie "Neu berechnen" in Schritt 2.',
            header: 'Berechnung starten',
            message: 'Die Verträge des Provisionslaufs werden jetzt bewertet und die Buchungen im Status '
                + '"berechnet" abgelegt. Fortfahren?',
            icon: 'fa-solid fa-calculator',
            acceptLabel: 'Ja, berechnen',
            actionKey: 'calculate',
            job: true,
            request: () => this.provisionRunService.calculate(this.provisionRunId)
        });
    }

    handleRestartCalculation(): void {
        this.confirmAndRun({
            eligible: this.provisionRunDto?.status === ProvisionRunStatus.CALCULATION_FAILED,
            blockedMessage: 'Die Berechnung ist nicht im Fehlerzustand — ein Wiederanlauf ist nicht möglich.',
            header: 'Berechnung erneut starten',
            message: 'Die fehlgeschlagene Berechnung wird erneut gestartet. Bereits berechnete Buchungen '
                + 'dieses Laufs werden dabei ersetzt. Fortfahren?',
            icon: 'fa-solid fa-rotate-right',
            acceptLabel: 'Ja, erneut starten',
            actionKey: 'restartCalculation',
            job: true,
            request: () => this.provisionRunService.calculate(this.provisionRunId)
        });
    }

    handleReview(): void {
        this.confirmAndRun({
            eligible: this.isEligibleReview(this.provisionRunDto?.status),
            blockedMessage: 'Der Provisionslauf kann nur aus dem Status "Berechnet" in die Prüfung gestellt werden.',
            header: 'In Prüfung stellen',
            message: 'Der Provisionslauf wird in die Prüfung gestellt. Die berechneten Buchungen bleiben '
                + 'unverändert und können weiterhin neu berechnet werden. Fortfahren?',
            icon: 'fa-solid fa-magnifying-glass',
            acceptLabel: 'Ja, prüfen',
            actionKey: 'review',
            job: false,
            request: () => this.provisionRunService.review(this.provisionRunId)
        });
    }

    /**
     * "Aktivieren" opens the activation dialog ({@link CpRunActivationConfirmDialogComponent}) with
     * sum, bookings, error contracts, M-026 warnings and the last calculation — the guards (right,
     * eligibility) run BEFORE it opens, exactly as for every other action.
     */
    handleActivate(): void {

        if (!this.checkActionAllowed(
            this.isEligibleActivate(this.provisionRunDto?.status),
            'Die Aktivierung ist erst möglich, wenn die Berechnung abgeschlossen ist.')) {
            return;
        }

        this.activationDialogVisible = true;
    }

    /** The operator confirmed in the activation dialog — fire the job like every other action. */
    handleActivationAccepted(): void {

        // the status may have moved while the dialog was open; the dialog is a snapshot as well
        if (!this.checkActionAllowed(
            this.isEligibleActivate(this.provisionRunDto?.status),
            'Die Aktivierung ist erst möglich, wenn die Berechnung abgeschlossen ist.')) {
            return;
        }

        this.runAction({
            actionKey: 'activate',
            job: true,
            request: () => this.provisionRunService.activate(this.provisionRunId)
        });
    }

    handleRestartActivation(): void {
        this.confirmAndRun({
            eligible: this.provisionRunDto?.status === ProvisionRunStatus.ACTIVATION_FAILED,
            blockedMessage: 'Die Aktivierung ist nicht im Fehlerzustand — ein Wiederanlauf ist nicht möglich.',
            header: 'Aktivierung erneut starten',
            message: 'Die fehlgeschlagene Aktivierung wird erneut gestartet. Bereits aktivierte Buchungen '
                + 'bleiben unverändert. Fortfahren?',
            icon: 'fa-solid fa-rotate-right',
            acceptLabel: 'Ja, erneut starten',
            actionKey: 'restartActivation',
            job: true,
            request: () => this.provisionRunService.activate(this.provisionRunId)
        });
    }

    handlePayout(): void {
        this.confirmAndRun({
            eligible: this.isEligiblePayout(this.provisionRunDto?.status),
            blockedMessage: 'Die Auszahlung ist erst möglich, wenn die Verträge aktiviert sind.',
            header: 'Auszahlung starten',
            message: 'Die auszahlbaren Beträge werden jetzt zur Auszahlung gestellt. Berater mit '
                + 'Unterdeckung auf dem Rücklagenkonto werden dabei von der Auszahlungsbremse '
                + 'zurückgehalten und im Reiter "Lauf-Meldungen" gemeldet. Fortfahren?',
            icon: 'fa-solid fa-money-bill-transfer',
            acceptLabel: 'Ja, auszahlen',
            actionKey: 'payout',
            job: true,
            request: () => this.provisionRunService.payout(this.provisionRunId)
        });
    }

    handleRestartPayout(): void {
        this.confirmAndRun({
            eligible: this.provisionRunDto?.status === ProvisionRunStatus.PAYOUT_FAILED,
            blockedMessage: 'Die Auszahlung ist nicht im Fehlerzustand — ein Wiederanlauf ist nicht möglich.',
            header: 'Auszahlung erneut starten',
            message: 'Die fehlgeschlagene Auszahlung wird erneut gestartet. Bereits ausgezahlte Beträge '
                + 'bleiben unverändert. Fortfahren?',
            icon: 'fa-solid fa-rotate-right',
            acceptLabel: 'Ja, erneut starten',
            actionKey: 'restartPayout',
            job: true,
            request: () => this.provisionRunService.payout(this.provisionRunId)
        });
    }

    handleComplete(): void {
        this.confirmAndRun({
            eligible: this.isEligibleComplete(this.provisionRunDto?.status),
            blockedMessage: 'Der Provisionslauf kann erst abgeschlossen werden, wenn die Auszahlung '
                + 'durchgeführt wurde.',
            header: 'Provisionslauf abschließen',
            message: 'Der Provisionslauf wird abgeschlossen. Danach ist der nächste Lauf dieses '
                + 'Provisionstyps freigegeben. Fortfahren?',
            icon: 'fa-solid fa-flag-checkered',
            acceptLabel: 'Ja, abschließen',
            actionKey: 'complete',
            job: false,
            request: () => this.provisionRunService.complete(this.provisionRunId)
        });
    }

    /**
     * Download the accounting hand-over of the run as a CSV file (03 §8.4 step 4, BR-080, original
     * requirement Z.261 "der Wert wird an die Buchhaltung gemeldet").
     *
     * NO confirmation dialog and NO busy raise on the whole bar: this action changes nothing, it
     * fetches a document. {@link busyReadOnlyAction} carries the spinner of THIS button only, and
     * doubles as the re-entry guard against a second click while the file is on its way (the bm
     * original guards a download the same way, with its `downloadingFiles` set).
     *
     * The "never return silently" rule of {@link confirmAndRun} applies here too: `disabled` is a
     * snapshot, so a click that arrives after the status moved on explains itself in German instead
     * of doing nothing at all.
     */
    handleAccountingExport(): void {

        // A download already in flight — a second click would save the same file twice.
        if (this.busyReadOnlyAction === 'accountingExport') {
            return;
        }

        if (!this.isEligibleAccountingExport(this.provisionRunDto?.status)) {
            this.showBlocked('Aktion nicht möglich',
                this.accountingExportDeniedByServer
                    ? 'Ihnen fehlt die Berechtigung, die Buchhaltungs-Meldung dieses Provisionslaufs '
                        + 'zu lesen.'
                    : 'Die Buchhaltungs-Meldung steht erst zur Verfügung, wenn die Auszahlung des '
                        + 'Provisionslaufs gebucht ist.');
            return;
        }

        this.busyReadOnlyAction = 'accountingExport';

        this.provisionRunService
            .downloadAccountingExportCsv(this.provisionRunId, this.buildAccountingExportFileName())
            .subscribe({
                error: (error) => {
                    this.busyReadOnlyAction = null;
                    this.handleAccountingExportError(error);
                },
                complete: () => this.busyReadOnlyAction = null
            });
    }

    /**
     * A 403 on the hand-over is the OBJECT right answering, not the feature right: the endpoint
     * checks `read` access on the run (`ProvisionRunController#getAccountingExport`). It therefore
     * demotes this ONE button — stickily, for the same reason {@link handleActionError} does — and
     * leaves the transition actions of the bar untouched.
     */
    private handleAccountingExportError(error: any): void {

        if (CpFeatureRight.isForbidden(error)) {
            this.accountingExportDeniedByServer = true;
            this.refreshComponent();
            this.showBlocked(CpFeatureRight.DENIED_HEADER,
                'Ihnen fehlt die Berechtigung, die Buchhaltungs-Meldung dieses Provisionslaufs zu lesen.');
            return;
        }

        this.showBlocked('Download fehlgeschlagen',
            'Die Buchhaltungs-Meldung konnte nicht geladen werden. Bitte prüfen Sie die Meldungen des '
            + 'Laufs und versuchen Sie es erneut.');
    }

    /**
     * Name the hand-over is saved under.
     *
     * The backend's own `Content-Disposition` names the file after the run ID only
     * (`cp-auszahlung-lauf-{id}.csv`) — unambiguous for a machine, useless in a bookkeeper's
     * download folder next to eleven other runs. The clerk hands this file to accounting, so the
     * name carries what identifies it there: the run name and the SETTLED PERIOD.
     *
     * German letters are kept (module rule: user-visible text with real umlauts); everything that
     * is not a letter or a digit collapses into a single hyphen, which is what keeps the name safe
     * on every file system.
     */
    private buildAccountingExportFileName(): string {

        const runName = this.toFileNamePart(this.provisionRunDto?.name) || ('lauf-' + this.provisionRunId);

        const period = [
            this.toFileDate(this.provisionRunDto?.periodStartDate),
            this.toFileDate(this.provisionRunDto?.periodEndDate)
        ].filter(part => !!part).join('-');

        return period
            ? `buchhaltungs-meldung-${runName}-${period}.csv`
            : `buchhaltungs-meldung-${runName}.csv`;
    }

    /** Lower-case, hyphen-joined form of a free-text value — letters (incl. umlauts) and digits survive. */
    private toFileNamePart(value: string | undefined): string {

        if (!value) {
            return '';
        }

        return value.trim().toLowerCase()
            .replace(/[^\p{L}\p{N}]+/gu, '-')
            .replace(/^-+|-+$/g, '');
    }

    /**
     * `yyyyMMdd` of a run date. The payload delivers ISO strings even where the DTO types a `Date`,
     * so both are accepted; an unreadable value drops out of the name instead of writing "NaN" into it.
     */
    private toFileDate(value: Date | string | undefined): string {

        if (!value) {
            return '';
        }

        const date = value instanceof Date ? value : new Date(value);

        if (isNaN(date.getTime())) {
            return '';
        }

        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');

        return `${date.getFullYear()}${month}${day}`;
    }

    /**
     * "Abrechnungen erstellen" — confirm, then start the job that issues the final statement of every
     * agent with movement in the run (`POST /{id}/agentStatements/create`, right `cp_agentStatement`).
     *
     * Guarded like every other action (never return silently), but by its OWN right: a missing
     * `cp_runExecution` does not block it, a 403 demotes only this button
     * ({@link handleAgentStatementsError}). On acceptance the job-started message is shown and the
     * bar follows the job exactly like the other job actions (fast polling, busy for 20s).
     */
    handleCreateAgentStatements(): void {

        if (this.busy || this.jobRunning) {
            this.showBlocked('Aktion nicht möglich',
                'Es läuft bereits ein Provisions-Job. Bitte warten Sie, bis er beendet ist.');
            return;
        }

        if (!this.isEligibleCreateAgentStatements(this.provisionRunDto?.status)) {
            this.showBlocked(
                this.agentStatementDeniedByServer ? CpFeatureRight.DENIED_HEADER : 'Aktion nicht möglich',
                this.agentStatementDeniedByServer
                    ? CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_STATEMENT)
                    : 'Abrechnungen können erst erstellt werden, wenn die Auszahlung des Provisionslaufs '
                        + 'vollständig gebucht ist (Status "Ausgezahlt" oder "Abgeschlossen").');
            return;
        }

        this.confirmationService.confirm({
            header: 'Abrechnungen erstellen',
            message: 'Für alle Berater mit Bewegung in diesem Provisionslauf werden jetzt die finalen '
                + 'Provisionsabrechnungen erstellt und im Dokumentenarchiv abgelegt. Bereits bestehende '
                + 'Abrechnungen werden nicht überschrieben — eine Korrektur ist nur über "Neu ausstellen" '
                + 'am Berater möglich. Fortfahren?',
            icon: 'fa-solid fa-file-invoice',
            acceptLabel: 'Ja, erstellen',
            rejectLabel: 'Abbrechen',
            accept: () => this.runCreateAgentStatements()
        });
    }

    /** The fire half of {@link handleCreateAgentStatements} — job timing as in {@link runAction}. */
    private runCreateAgentStatements(): void {

        this.busyAction = 'agentStatements';
        this.busy = true;

        this.agentStatementsSubscription?.unsubscribe();
        const request$ = this.provisionRunService.createAgentStatements$(this.provisionRunId);
        this.agentStatementsSubscription = request$.subscribe({
            next: (response: CoreApiResponseDto) => {
                // A business refusal may come back as HTTP 200 with success = false.
                if (response && response.success === false) {
                    this.clearBusy();
                    this.showBlocked('Abrechnungen nicht gestartet',
                        response.cause || 'Der Job zur Erstellung der Abrechnungen konnte nicht gestartet werden.');
                    return;
                }

                this.messageService.showSuccessMessage('Abrechnungen erstellen',
                    'Der Job zur Erstellung der Provisionsabrechnungen wurde gestartet. Die Abrechnungen '
                    + 'erscheinen nach Abschluss in „Summe je Berater“ und am Berater.');
                this.schedule(() => this.setFastPollingMode(), 3000);
                this.schedule(() => this.clearBusy(), 20000);
            },
            error: (error) => {
                this.clearBusy();
                this.handleAgentStatementsError(error);
            }
        });
    }

    /**
     * A 403 is the right `cp_agentStatement` answering: demote THIS button stickily and leave the
     * rest of the bar alone (its right is `cp_runExecution`).
     */
    private handleAgentStatementsError(error: any): void {

        if (CpFeatureRight.isForbidden(error)) {
            this.agentStatementDeniedByServer = true;
            this.refreshComponent();
            this.showBlocked(CpFeatureRight.DENIED_HEADER,
                CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_STATEMENT));
            return;
        }

        // 409 (run not PAID_OUT / COMPLETED) and the like: the server's German sentence, if it sent one
        cpServerMessage(error)
            .catch(() => null)
            .then((serverMessage: string | null) => this.showBlocked('Abrechnungen nicht gestartet',
                serverMessage
                ?? 'Der Job zur Erstellung der Abrechnungen konnte nicht gestartet werden. Bitte prüfen Sie die '
                + 'Meldungen des Laufs und versuchen Sie es erneut.'));
    }

    /**
     * Abort the run. Deliberately NOT a step of the bar: 04 §5.1 lists it without a step, because it
     * is reachable from every status up to and including IN_REVIEW / CALCULATION_FAILED — so it lives
     * in the header band above the bar instead of being repeated on four rows.
     */
    handleAbort(): void {
        this.confirmAndRun({
            eligible: this.isEligibleAbort(this.provisionRunDto?.status),
            blockedMessage: 'Der Provisionslauf kann nicht mehr abgebrochen werden. Ein Abbruch ist nur '
                + 'möglich, solange noch keine Buchung aktiviert wurde.',
            header: 'Provisionslauf abbrechen',
            message: 'Der Provisionslauf wird abgebrochen. ALLE berechneten, noch nicht aktivierten '
                + 'Buchungen dieses Laufs werden dabei verworfen. Dieser Schritt kann nicht rückgängig '
                + 'gemacht werden. Provisionslauf wirklich abbrechen?',
            icon: 'fa-solid fa-triangle-exclamation',
            acceptLabel: 'Ja, abbrechen',
            actionKey: 'abort',
            job: false,
            request: () => this.provisionRunService.abort(this.provisionRunId)
        });
    }

    /**
     * The ONE place that confirms an action, fires it, and manages busy/polling afterwards.
     *
     * The bm original repeats this block once per action; folding it into a single method keeps the
     * eight cp actions from drifting apart, while the BEHAVIOUR is unchanged and is documented here
     * once instead of eight times:
     *
     * NEVER RETURN SILENTLY. `disabled` is a SNAPSHOT taken by initEventsFlow(), so a command is
     * reachable from a button that still looks clickable whenever the state moved on after the
     * timeline was built. A click that produces nothing at all is indistinguishable from a broken
     * page — so an ineligible action explains itself in German instead.
     *
     * TIMING STRATEGY (job actions). When the HTTP call returns, the Spring Batch job may not have
     * started yet in the backend; the indicator polls on an interval and would report "no running
     * jobs", re-enabling every button prematurely. Therefore: wait 3s before switching to fast
     * polling (time for the backend to start the job), and keep `busy` raised for 20s (at least one
     * full 15s poll cycle) so the buttons stay dead until `jobRunning` can carry them.
     *
     * NON-JOB actions (review / complete / abort) are plain state transitions, but the endpoints are
     * @Async on the backend as well, so an immediate re-read can still answer with the OLD status.
     * They re-read after a short delay and are re-read again on every tab change of the host.
     */
    private confirmAndRun(config: {
        eligible: boolean;
        blockedMessage: string;
        header: string;
        message: string;
        icon: string;
        acceptLabel: string;
        actionKey: string;
        job: boolean;
        request: () => Observable<CoreApiResponseDto>;
    }): void {

        if (!this.checkActionAllowed(config.eligible, config.blockedMessage)) {
            return;
        }

        this.confirmationService.confirm({
            header: config.header,
            message: config.message,
            icon: config.icon,
            acceptLabel: config.acceptLabel,
            rejectLabel: 'Abbrechen',
            accept: () => this.runAction(config)
        });
    }

    /**
     * The guard half of {@link confirmAndRun}: right first, then eligibility — each refusal says
     * why in German instead of returning silently.
     *
     * @return true when the action may proceed
     */
    private checkActionAllowed(eligible: boolean, blockedMessage: string): boolean {

        if (!this.runExecutionGranted) {
            this.showBlocked(CpFeatureRight.DENIED_HEADER,
                CpFeatureRight.deniedMessage(CpFeatureRight.RUN_EXECUTION));
            return false;
        }

        if (!eligible) {
            this.showBlocked('Aktion nicht möglich', blockedMessage);
            return false;
        }

        return true;
    }

    /**
     * The fire half of {@link confirmAndRun}: busy, request, then the timing strategy described
     * there. Shared with the activation dialog, which confirms through its own UI.
     */
    private runAction(config: {
        actionKey: string;
        job: boolean;
        request: () => Observable<CoreApiResponseDto>;
    }): void {

        this.busyAction = config.actionKey;
        this.busy = true;

        config.request().subscribe({
            next: () => {
                // Request accepted by the backend - the job/transition runs asynchronously.
            },
            error: (error) => {
                this.busy = false;
                this.busyAction = null;
                this.handleActionError(error);
            },
            complete: () => {
                if (config.job) {
                    this.schedule(() => this.setFastPollingMode(), 3000);
                    this.schedule(() => this.clearBusy(), 20000);
                } else {
                    this.schedule(() => {
                        this.clearBusy();
                        this.retrieveProvisionRun();
                    }, 1000);
                }
            }
        });
    }

    /**
     * A 403 is the um right `cp_runExecution` answering. Demote the mirror so the whole bar goes
     * quiet instead of offering controls the server will keep refusing — the demotion half of the
     * module-wide optimistic pattern (RIGHTS section of the class TSDoc, {@link CpFeatureRight}).
     */
    private handleActionError(error: any): void {

        if (CpFeatureRight.isForbidden(error)) {
            this.runExecutionDeniedByServer = true;
            this.refreshComponent();
            this.showBlocked(CpFeatureRight.DENIED_HEADER,
                CpFeatureRight.deniedMessage(CpFeatureRight.RUN_EXECUTION));
            return;
        }

        this.showBlocked('Aktion fehlgeschlagen',
            'Die Aktion konnte nicht ausgeführt werden. Bitte prüfen Sie die Meldungen des Laufs '
            + 'und versuchen Sie es erneut.');
    }

    /** Information-only dialog (no reject button) — the "say why" path of an unavailable action. */
    private showBlocked(header: string, message: string): void {
        this.confirmationService.confirm({
            header: header,
            message: message,
            icon: 'fa-solid fa-circle-info',
            acceptLabel: 'OK',
            rejectVisible: false
        });
    }

    private clearBusy(): void {
        this.busy = false;
        this.busyAction = null;
    }

    /** Tracked setTimeout — every pending timer is cleared in ngOnDestroy. */
    private schedule(action: () => void, delayMs: number): void {
        const handle = window.setTimeout(() => {
            this.pendingTimeouts = this.pendingTimeouts.filter(id => id !== handle);
            action();
        }, delayMs);
        this.pendingTimeouts.push(handle);
    }

    /**
     * Process changes within binding.
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        if (changes['provisionRunDto'] && this.provisionRunDto) {
            this.provisionRunId = this.provisionRunDto.id;
            this.refreshComponent();
        }

        if (changes['provisionRunId'] && !changes['provisionRunDto']) {
            this.retrieveProvisionRun();
        }

        // The host revised its answer about the right — re-resolve and rebuild, so the controls
        // follow the grant without the page having to re-create the component.
        if (changes['runExecutionAllowed']) {
            this.refreshComponent();
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.statusSubscription?.unsubscribe();
        this.errorCountSubscription?.unsubscribe();
        this.agentStatementsSubscription?.unsubscribe();
        this.pendingTimeouts.forEach(handle => window.clearTimeout(handle));
        this.pendingTimeouts = [];
    }

    protected readonly ProvisionRunStatus = ProvisionRunStatus;
}
